import * as React from "react"
import * as ReactDOM from "react-dom"
import {CallJs as CallJsFunction} from "../lib/types"
import * as $ from "jquery"
import {Modal} from "../lib/sbhtml/modal"
import {ManagedForm} from "../lib/sbhtml/form"
import {Group, Item} from "../lib/sbhtml/field"
import {CommandCaller} from "../lib/commands"
import {SingleBox as Select} from "../lib/sbhtml/select"
import {Input} from "../lib/sbhtml/input"

interface ListPageParams {
	buttonId: string
	securePayPage: string
	ajaxPage: string
	dataGridId: string
}

//noinspection JSUnusedGlobalSymbols
export const listPage:CallJsFunction<ListPageParams> = (cb, props) => {
	const div = document.createElement('div')
	cb(div)
	const modal = ReactDOM.render(<MyModal
		securePayPage={props.securePayPage}
		ajaxPage={props.ajaxPage}
		isOpen={false}
		dataGridId={props.dataGridId}
	/>, div) as MyModal
	$("#" + props.buttonId).click(() => modal.setState(s => ({...s, isOpen: true})))
}

interface MyState {
	isOpen: boolean
	stageName: StageName
	ewayAccountSettings: EwayAccountSettings
}

interface MyProps {
	isOpen: boolean
	securePayPage: string
	ajaxPage: string
	dataGridId: string
}

/**
 * We have to use an Enum for the stage rather than a class instance because it has to go in the state of the React
 * component and I don't think React copes well with class instances in the component state.
 *
 * A normal enum allows bidirectional name <=> int conversion, which can make refactoring difficult. A "const" enum is
 * resolved entirely at compile-time and so is more predictable.
 */
const enum StageName {
	Step1,
	SecurePay,
	eWayRapid,
}

class MyModal extends React.Component<MyProps, MyState> {
	constructor(props:MyProps, context?:any) {
		super(props, context)
		this.state = {
			isOpen:              props.isOpen,
			stageName:           StageName.Step1,
			ewayAccountSettings: MyModal.blankEwayAccountSettings
		}
	}

	private static blankEwayAccountSettings:EwayAccountSettings = {
		name:       "",
		password:   "",
		apiKey:     "",
		isTestMode: true
	}

	private ewayChanged(e:EwayAccountSettings) {
		this.setState((s) => {
			s.ewayAccountSettings = e
			return s
		})
	}

	private submitNewEwayAccount() {
		const c = new CommandCaller(this.props.ajaxPage)
		c.runPost('eway.new account', this.state.ewayAccountSettings).then(() => {
			this.refresh()
			this.close()
		})
	}

	private stageFromName(name:StageName):MyModalController {
		switch (name) {
			case StageName.Step1:
				return new Step1Controller((c) => this.selectType(c))
			case StageName.SecurePay:
				return new SecurePayController(this.props.securePayPage)
			case StageName.eWayRapid:
				return new EwayRapidController(
					() => { this.submitNewEwayAccount() },
					this.state.ewayAccountSettings,
					(e) => { this.ewayChanged(e) }
				)
			default:
				throw new Error(`unknown stage '${name}'`)
		}
	}

	private selectType(stageName:StageName) {
		this.setState((s) => {
			s.isOpen    = true
			s.stageName = stageName
			return s
		})
	}

	private refresh() {
		new HtmlDataGrid(this.props.dataGridId).refresh()
	}

	private close() {
		this.setState({
			isOpen:              false,
			stageName:           StageName.Step1,
			ewayAccountSettings: MyModal.blankEwayAccountSettings
		})
	}

	render() {
		const stage = this.stageFromName(this.state.stageName)
		stage.start()

		return <Modal
			style={{width: "400px"}}
			title={stage.title()}
			isOpen={this.state.isOpen}
			onRequestClose={() => this.close()}
		>
			{stage.body()}
		</Modal>
	}
}

abstract class MyModalController {
	abstract title():string

	abstract body():React.ReactElement<{}>

	start():void {}
}

interface Step1Props {
	onSelect: (c:StageName) => void
}

class Step1Controller extends MyModalController {
	constructor(private onSelect:(c:StageName) => void) {
		super()
	}

	body() { return <Step1Modal onSelect={this.onSelect}/> }

	title() { return "Step 1. Select type" }
}

class Step1Modal extends React.Component<Step1Props, {}> {
	render() {
		return <ManagedForm onSubmit={() => Promise.resolve()}>
			<Group>
				<Item label="SecurePay">
					<button type="button" onClick={() => this.props.onSelect(StageName.SecurePay)}>SecurePay</button>
				</Item>
				<Item label="eWay Rapid">
					<button type="button" onClick={() => this.props.onSelect(StageName.eWayRapid)}>eWay Rapid</button>
				</Item>
			</Group>
		</ManagedForm>
	}
}

class SecurePayController extends MyModalController {
	constructor(private securePayPage:string) {
		super()
	}

	title():string {
		return "Step 2. SecurePay"
	}

	body() { return <div>Please wait to be redirected...</div> }

	start():void { window.location.assign(this.securePayPage) }
}

class EwayRapidController extends MyModalController {
	constructor(
		private done:() => void,
		private settings:EwayAccountSettings,
		private onChange:EwayChangeHandler
	) {
		super()
	}

	title():string { return "Step 2. Configure eWay Rapid" }

	body() {
		// TODO Port to async/await
		const done = () => {
			if (this.settings.name.length > 0) {
				this.done()
			} else {
				alert('Please enter a name')
			}
			return Promise.resolve()
		}

		return <ManagedForm hasSave={true} onSubmit={done}>
			<EwaySettings onChange={this.onChange} settings={this.settings}/>
		</ManagedForm>
	}
}

interface EwayAccountSettings {
	name: string
	apiKey: string
	password: string
	isTestMode: boolean
}

interface EwayAccountSettingsPartial {
	name?: string
	apiKey?: string
	password?: string
	isTestMode?: boolean
}

interface EwayProps {
	settings: EwayAccountSettings
	onChange: EwayChangeHandler
}

class EwaySettings extends React.Component<EwayProps, {}> {
	private updateWithObject(a:EwayAccountSettingsPartial) {
		this.props.onChange({...this.props.settings, ...a})
	}

	private setName(value:string) {
		this.updateWithObject({name: value})
	}

	private setApiKey(value:string) {
		this.updateWithObject({apiKey: value})
	}

	private setPassword(value:string) {
		this.updateWithObject({password: value})
	}

	private setTestMode(value:string) {
		this.updateWithObject({isTestMode: value == "test"})
	}

	render() {
		return <Group>
			<Item label="Name">
				<Input onChange={(value) => this.setName(value)} value={this.props.settings.name}/>
			</Item>
			<Item label="API key">
				<Input onChange={(value) => this.setApiKey(value)} value={this.props.settings.apiKey}/>
			</Item>
			<Item label="Password">
				<Input onChange={(value) => this.setPassword(value)} value={this.props.settings.password}/>
			</Item>
			<Item label="Mode">
				<Select
					onChange={(value) => this.setTestMode(value)}
					value={this.props.settings.isTestMode ? "test" : "live"}
				>
					<option value="test">Test</option>
					<option value="live">Live</option>
				</Select>
			</Item>
		</Group>
	}
}

interface EditEwayProps {
	settings: EwayAccountSettings
	onFinished: (settings:EwayAccountSettings) => Promise<void>
}

interface EditEwayState {
	settings: EwayAccountSettings
	isOpen: boolean
}

export interface EditEwayCallJsProps {
	id: number
	ajaxPage: string
	dataGridId: string
}

//noinspection JSUnusedGlobalSymbols
export const editEwaySettings = (props:EditEwayCallJsProps) => {
	const cc  = new CommandCaller(props.ajaxPage)
	const div = document.createElement('div')
	document.body.appendChild(div)

	;(cc.runGet('eway.get settings', {id: props.id}) as Promise<EwayAccountSettings>).then((s:EwayAccountSettings) => {
		ReactDOM.render(
			<EditEwaySettingsModal
				settings={s}
				onFinished={(s) =>
				cc.runPost('eway.set settings', {id: props.id, settings: s})
					.then((c) => {
						new HtmlDataGrid(props.dataGridId).refresh()
					})
				}
			/>,
			div
		)
	})
}

class EditEwaySettingsModal extends React.Component<EditEwayProps, EditEwayState> {
	constructor(p:EditEwayProps, context?:any) {
		super(p, context)
		this.state = {
			settings: p.settings,
			isOpen:   true
		}
	}

	private onChange(settings:EwayAccountSettings) {
		this.setState((s) => {
			s.settings = settings
			return s
		})
	}

	private onSave() {
		return this.props.onFinished(this.state.settings)
	}

	private close() {
		this.setState({settings: this.props.settings, isOpen: false})
	}

	private onSubmit() {
		// TODO Write with async/await
		if (!this.state.settings.name) {
			alert('Please provide a name')
			return Promise.resolve()
		}
		return this.onSave().then(() => {this.close()})
	}

	render() {
		const title = "Editing " + this.state.settings.name
		const close = () => this.close()

		return <Modal
			style={{width: "400px"}}
			title={title}
			isOpen={this.state.isOpen}
			onRequestClose={close}
		>
			<ManagedForm hasSave={true} hasCancel={true}
				onSubmit={() => this.onSubmit()}
				onCancel={close}
			>
				<EwaySettings onChange={(s) => this.onChange(s)} settings={this.state.settings}/>
			</ManagedForm>
		</Modal>
	}
}

type EwayChangeHandler = (e:EwayAccountSettings) => void



// WEBPACK FOOTER //
// ./src/orders2/gateway-setup.tsx